import React, { Component, Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { injectIntl, FormattedMessage } from 'react-intl';
import { Row, Col, Card, Badge, Button } from 'react-bootstrap';
import VisibilitySensor from 'react-visibility-sensor';
import { Link } from 'react-router-dom';

import ReactGA4 from 'react-ga4';

import { getFile } from '../../../../util/file.js';
import { truncate, stripTags } from '../../../../util/text.js';
import { isMobile } from '../../../../util/browser.js';

import { getProductSlug, getProductPrice, getProductSpecialPrice, getProductSpecialPriceDateThreshold, getProductSpecialPrices } from '../../ProductActions.js';
import { getUserGroup, getUserGroupOption } from '../../../User/UserGroupActions.js';
import { getTranslation } from '../../../Intl/IntlHelper.js';
import { getReferral } from '../../../App/AppActions.js';

import withRouter from '../../../../components/Router/WithRouter.js';
import Icon from '../../../../components/Content/Icon.js';
import FileLoader from '../../../../components/Content/FileLoader.js';
import LazyLoader from '../../../../components/Content/LazyLoader.js';
import HtmlComponent from '../../../../components/Text/HtmlComponent.js';
import ProductWeight from '../view/ProductWeight.js';
import ProductAddToCart from '../form/ProductAddToCart.js';
import Price from '../../../Currency/components/view/Price.js';
import PredictiveLink from '../../../Cms/components/view/PredictiveLink.js';
import Chrono from '../../../../components/Time/Chrono.js';
import config from '../../../../config.js';
import { formatAnalyticsItems, getCartItemFromProduct } from '../../../Cart/CartActions.js';

function ProductListItem({ navigate, intl, product, userGroup = null, isOrder = false, hideAddToCart = false, viewType = 'grid', referral = null, trackSelection = false, listId = '' }) {
	const [mounted, setMounted] = useState(false);

	useEffect(() => {
		setMounted(true);
	}, []);

    const displayPriceHT = getUserGroupOption(userGroup, 'displayPriceHT');
    const productPrice = getProductPrice(product, null, userGroup, !displayPriceHT, referral);
	const productSpecialPrice = getProductSpecialPrice(product, displayPriceHT ? 'priceHT' : 'priceTTC', null, userGroup, referral);
    const [activePicture, setActivePicture] = useState(null);

    const getDiscount = () => {
        return 100 - Math.round((productPrice * 100) / product[displayPriceHT ? 'priceHT' : 'priceTTC']);
    };

    const renderBenefits = () => {
        const benefits = getTranslation(product, 'benefits', intl.locale).sort();
        return benefits.length > 0 && (
            <div className="d-flex flex-wrap gap-1">
                {benefits.slice(0, 2).map(benefit => <Badge key={benefit} bg="info" className="rounded-pill">{benefit}</Badge>)}
            </div>
        );
    };

    const getProductUrl = () => `/${intl.locale}/${getProductSlug(product, intl.locale)}`;

	const handleTrackAnalytics = () => {
		if(typeof window !== 'undefined' && config.google.analytics.trackingCodeGA4) {
            // GA: New
            ReactGA4.event('select_item', {
				item_list_id: listId,
				items: formatAnalyticsItems([getCartItemFromProduct(product, 1, 'fr', userGroup, { referral })]),
			});
        }
        if(typeof window !== 'undefined' && window.fbq) {
            // TODO: Track login on Fb Pixel
        }
	};

    const viewProduct = () => {
        // navigate(getProductUrl());
		trackSelection && handleTrackAnalytics();
    };

    // Rend toute la carte cliquable, sans intercepter les éléments interactifs (liens, bouton d'ajout au panier...)
    const handleCardClick = event => {
        if(event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.button > 0) {
            return;
        }
        if(event.target.closest('a, button, input, select, textarea, label, [role="button"]')) {
            return;
        }
        viewProduct();
        navigate(getProductUrl());
    };

    const { locale } = intl;

    // Indique au survol que l'encart est cliquable
    const renderViewHint = () => (
        <span className="icon-box icon-box-light shadow" aria-hidden="true">
            <Icon icon="eye" size="2x" />
        </span>
    );

    const renderPicture = (aspectRatio = '4 / 3') => {
        const pictures = product.pictures || [];
        const video = pictures.find(picture => picture.includes('.gif') || picture.includes('.webm') || picture.includes('.mp4'));
        const image = pictures.find(picture => picture !== video);
        const wrapperStyle = { aspectRatio, overflow: 'hidden' };
        const imageStyle = { width: '100%', height: '100%', objectFit: 'cover' };

        if(video) {
            if(mounted && isMobile('screen')) {
                return (
                    <div className="product-image-wrapper" style={wrapperStyle}>
                        <VisibilitySensor active={!activePicture} onChange={visible => visible && setActivePicture(video)}>
                            {activePicture ? <FileLoader filename={activePicture} useAppUrl poster={getFile(image)} autoPlay loop muted loadonmount className="card-img rounded-bottom-0" style={imageStyle} /> : <span>{'&nbsp;'}</span>}
                        </VisibilitySensor>
                        {renderViewHint()}
                    </div>
                );
            }

            if(activePicture) {
                return (
                    <div className="product-image-wrapper" style={wrapperStyle}>
                        <FileLoader filename={activePicture} useAppUrl poster={getFile(image)} autoPlay loop muted loadonmount className="card-img rounded-bottom-0" style={imageStyle} onMouseOut={() => process.env.NODE_ENV === 'production' && setActivePicture(null)} />
                        {renderViewHint()}
                    </div>
                );
            }
        }

        return (
            <div className="product-image-wrapper" style={wrapperStyle}>
                {pictures.length > 0 && (
                    <img src={getFile(product.pictures[0])} alt={getTranslation(product, 'name', locale)} className="card-img rounded-bottom-0" style={imageStyle} onMouseOver={() => video && setActivePicture(video)} loading="lazy" />
                )}
                {renderViewHint()}
            </div>
        );
    };

    const renderPrice = () => {
		const specialPricseByQuantity = getProductSpecialPrices(product, displayPriceHT ? 'priceHT' : 'priceTTC', null, userGroup, referral).filter(specialPrice => specialPrice.quantity);
		const productSpecialPrice = specialPricseByQuantity.length > 0 && (getProductPrice(product, specialPricseByQuantity[0].quantity, userGroup, !displayPriceHT, referral) / specialPricseByQuantity[0].quantity);
		const finalPrice = productSpecialPrice || productPrice;
        return (
            <Fragment>
                {/* <small className="d-none"><FormattedMessage id="productUnitPrice" defaultMessage="Unit price" /></small> */}
                {finalPrice > 0 && finalPrice < product[displayPriceHT ? 'priceHT' : 'priceTTC'] && <small className="d-inline-block text-lg me-1 text-black-75 text-crossed"><Price amount={product[displayPriceHT ? 'priceHT' : 'priceTTC']} formatPrice /></small>}
                {productSpecialPrice && <em><small><br /><FormattedMessage id="productSpecialPriceFrom" defaultMessage="From" />{' '}</small></em>}
                <span className="h3 mb-0 text-danger"><Price amount={finalPrice} formatPrice /></span><br />
                {displayPriceHT && <small className="text-black-75"><em><FormattedMessage id="productPriceHT" defaultMessage="Excl. tax" /></em></small>}
            </Fragment>
        );
    };

    const renderDiscount = () => {
        const hasDiscount = productPrice > 0 && productPrice < product[displayPriceHT ? 'priceHT' : 'priceTTC'];
        const countdownDate = mounted && productSpecialPrice && getProductSpecialPriceDateThreshold(productSpecialPrice);
        if(!hasDiscount && !countdownDate) {
            return null;
        }
        return (
			<Fragment>
				<div className="position-absolute top-0 end-0 m-2 d-flex flex-column align-items-start gap-1">
					{hasDiscount && <Badge bg="danger" className="fs-6 fw-bold px-2 py-1 shadow-sm rounded-pill">-{getDiscount()}%</Badge>}
				</div>
				<div className="position-absolute bottom-0 start-0 w-100 mb-2 px-2 d-flex flex-column align-items-start gap-1 text-center">
					{countdownDate && (
						<span className="d-block w-100 text-danger p-1 rounded-5 bg-light bg-opacity-75">
							<Icon icon="danger" /> <span className="d-inline d-lg-none"><Chrono date={countdownDate} /></span><span className="d-none d-lg-inline"><FormattedMessage id="productSpecialPriceDateThresholdLimit" defaultMessage="Offer ends in {chrono}" values={{ chrono: <Chrono date={countdownDate} /> }} /></span>
						</span>
					)}
				</div>

			</Fragment>
        );
    };

    const renderLinks = () => {
		if(product.customizations?.length) {
			return <PredictiveLink tag="a" to={getProductUrl()} usePrefetch onClick={viewProduct} className="product-link btn btn-sm w-100 btn-outline-dark text-dark rounded-5 lh-sm"><FormattedMessage id="productCustomize" defaultMessage="Customize this product" /> <Icon icon="chevron-right" /></PredictiveLink>;
		}
        return !hideAddToCart && (
            product.isAvailable ? <ProductAddToCart product={product} listId={listId} color="outline-secondary" size="sm" className="w-100 rounded-pill lh-sm" /> : <Button variant="link" className="w-100 m-0 p-0 border-0 text-danger opacity-50"><FormattedMessage id="productNotAvailable" defaultMessage="Not available" /></Button>
        );
    };

    if(viewType === 'list') {
		const description = getTranslation(product, 'description', locale);
        return (
            <Card className="mx-2 mx-md-0 mb-4 product-list-item overflow-hidden rounded" onClick={handleCardClick}>
                <Row>
                    <Col xs="12" md="4">
                        {renderPicture('16 / 9')}
                    </Col>
                    <Col>
                        <div className="py-3 px-2 px-md-0">
                            <div className="d-flex d-md-block justify-content-between">
								<h3 className="d-block h4 mb-1 text-start" style={{ minHeight: '1.8em' }}><PredictiveLink tag="a" to={getProductUrl()} usePrefetch onClick={viewProduct} className="product-link">{getTranslation(product, 'name', locale)}</PredictiveLink></h3>
								<small><ProductWeight product={product} /></small>
                            </div>

                            <div className="mt-2">
                                {description && <HtmlComponent text={`${truncate(stripTags(description), 50)}...`} />}
                                {false && <div className="text-center text-md-start"><small className="text-primary"><Icon icon="cube" /> <FormattedMessage id="productPacking" defaultMessage="Box of {count} unit(s)" values={{ count: product.packing }} /></small></div>}
                                <div className="d-block d-md-none mt-3 text-center">
                                    {renderPrice()}
                                </div>
                            </div>
                        </div>
                    </Col>
                    <Col xs="12" md="3">
                        <div className="p-md-3">
                            <div className="d-none d-md-block text-center">
                                {renderPrice()}
                                {renderDiscount()}
                            </div>

                            {renderLinks()}
                        </div>
                    </Col>
                </Row>
            </Card>
        );
    }
    if(viewType === 'mini') {
        return (
            <Card className="product-list-item product-list-item-mini h-100 bg-white shadow-sm overflow-hidden rounded-3 text-center" onClick={handleCardClick}>
                <div className="position-relative">
                    <div className="product-image-wrapper card-img rounded-bottom-0" style={{ aspectRatio: '1', overflow: 'hidden' }}>
                        {product.pictures.length > 0 && (
							<img src={getFile(product.pictures[0])} alt={getTranslation(product, 'subtitle', locale) || getTranslation(product, 'name', locale)} width="300" height="300" style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
                        )}
                        {renderViewHint()}
                    </div>
                    {renderDiscount()}
                </div>
                <Card.Body className="d-flex flex-column p-2">
                    {/* h3 (not h4): the parent section title is an h2, so h4 would skip a level. Visual size stays small via the utility classes. */}
                    <h3 className="small fw-semibold lh-sm mb-1" style={{ minHeight: '1.8em' }}><a href={getProductUrl()} onClick={viewProduct} className="product-link">{getTranslation(product, 'name', locale)}</a></h3>
                    <div className="mb-1 mt-auto">
                        {productPrice > 0 && productPrice < product[displayPriceHT ? 'priceHT' : 'priceTTC'] && <small className="d-inline-block me-1 text-black-75 text-crossed"><Price amount={product[displayPriceHT ? 'priceHT' : 'priceTTC']} formatPrice /></small>}
                        <span className="d-inline h4 fw-bold mb-0 text-danger"><Price amount={productPrice} formatPrice /></span>
                        {displayPriceHT && <small className="text-black-75 ms-1"><em><FormattedMessage id="productPriceHT" defaultMessage="Excl. tax" /></em></small>}
                    </div>
					{renderLinks()}
                    {/* {!hideAddToCart && (product.isAvailable && !product.customizations?.length
						? <ProductAddToCart product={product} listId={listId} hideCartButton color="outline-secondary" size="sm" className="rounded-pill py-1 px-2 lh-sm" />
						: <Button variant="link" size="sm" className="w-100 m-0 p-0 border-0 text-danger small"><FormattedMessage id="productNotAvailable" defaultMessage="Not available" /></Button>)} */}
                </Card.Body>
            </Card>
        );
    }
    // <FileLoader filename={product.pictures[0]} alt={getTranslation(product, 'name', locale)} className="card-img" style={{ maxWidth: '100%' }} />
    return (
        <Card className={classnames('product-list-item post-card h-100 shadow-sm overflow-hidden rounded-3', isOrder ? 'me-3' : 'mb-4')} onClick={handleCardClick}>
            <div className="position-relative">
                {renderPicture()}
                {renderDiscount()}
            </div>
            <Card.Body className="d-flex flex-column py-2 px-0">
				<h3 className="d-block h6 lh-sm mb-1 pb-0 text-start text-center" style={{ minHeight: '1.8em' }}><PredictiveLink tag="a" to={getProductUrl()} usePrefetch onClick={viewProduct} className="product-link text-decoration-none">{getTranslation(product, 'name', locale)}</PredictiveLink></h3>
				<div className="mt-1 px-2 d-flex align-items-center justify-content-center gap-3 flex-wrap">
					<small className="text-muted"><ProductWeight product={product} /></small>
                    <span className="d-flex align-items-baseline gap-1 text-center">
                        {productPrice > 0 && productPrice < product[displayPriceHT ? 'priceHT' : 'priceTTC'] && <small className="text-black-75 text-crossed"><Price amount={product[displayPriceHT ? 'priceHT' : 'priceTTC']} formatPrice /></small>}
                        <span className="h4 mb-0 fw-bold text-danger"><Price amount={productPrice} formatPrice /></span>
                        {displayPriceHT && <small className="text-black-75"><em> <FormattedMessage id="productPriceHT" defaultMessage="Excl. tax" /></em></small>}
                    </span>
				</div>
				<div className="px-1">
					{renderLinks()}
				</div>
            </Card.Body>
        </Card>
    );
}
// <Button variant="secondary" size="sm" outline block><Icon icon="eye" /></Button>

function mapStateToProps(store, props) {
    return {
        userGroup: getUserGroup(store),
        referral: getReferral(store),
    };
}

ProductListItem.propTypes = {
    navigate: PropTypes.object.isRequired,
    intl: PropTypes.object.isRequired,
    userGroup: PropTypes.object,
    product: PropTypes.object.isRequired,
    viewType: PropTypes.string,
    isOrder: PropTypes.bool,
    hideAddToCart: PropTypes.bool,
    referral: PropTypes.string,
	listId: PropTypes.string,
	trackSelection: PropTypes.bool,
};

export default connect(mapStateToProps)(withRouter(injectIntl(ProductListItem)));
